'use client';

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { CarPulseLogo } from '@/features/auth/components/car_pulse_logo';
import Input from '@/ui/input/input';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe,
  Calendar,
  Clock,
  Bell,
} from 'lucide-react';
import { Toggle } from '@/ui/toggle';
import { LOGIN_ROUTE } from '@/route';
import { formatDate } from '@/libs/utils';
import { Skeleton } from '@heroui/react';
import { MarkAsDropdown } from '@/ui/mark-as-dropdown/mark-as-dropdown';
import { DEFAULT_PUBLIC_DEMOS_USER_TYPES } from '@/features/prospect/types';
import {
  useCreatePublicDemoSession,
  usePatchPublicDemoSession,
  usePublicDemosAvailability,
  usePublicDemosMeta,
} from '@/features/prospect/hooks/hooks';

export default function PlanifierDemoPage() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const DEBUG_PUBLIC_DEMOS = process.env.NODE_ENV !== 'production';
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    email: '',
    phone: '',
    userType: '', // API value (ex: "individual")
    timezone:
      (typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined) || 'Europe/Paris',
    selectedDate: null as Date | null,
    selectedSlotId: '',
    platform: 'google_meet', // API value (ex: "zoom", "google_meet", "teams", "other")
    platformLink: '',
    message: '',
    notificationsEnabled: true,
  });
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [mounted, setMounted] = useState(false);
  const timezoneRef = useRef<HTMLDivElement>(null);
  const timezoneButtonRef = useRef<HTMLButtonElement>(null);

  const metaQuery = usePublicDemosMeta();
  const availabilityQuery = usePublicDemosAvailability({
    month: currentMonth.getMonth() + 1,
    year: currentMonth.getFullYear(),
    timezone: formData.timezone,
  });
  const createSessionMutation = useCreatePublicDemoSession();
  const patchSessionMutation = usePatchPublicDemoSession();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!DEBUG_PUBLIC_DEMOS) return;
    if (metaQuery.isError) {
      const err = metaQuery.error;
      const errInfo =
        err instanceof Error
          ? { name: err.name, message: err.message, stack: err.stack }
          : { value: err };
      // eslint-disable-next-line no-console
      console.error('[planifier-demo] metaQuery error', {
        status: metaQuery.status,
        fetchStatus: metaQuery.fetchStatus,
        error: errInfo,
      });
    }
  }, [
    DEBUG_PUBLIC_DEMOS,
    metaQuery.isError,
    metaQuery.status,
    metaQuery.fetchStatus,
    metaQuery.error,
  ]);

  useEffect(() => {
    if (!DEBUG_PUBLIC_DEMOS) return;
    if (metaQuery.isSuccess) {
      // eslint-disable-next-line no-console
      console.log('[planifier-demo] metaQuery success', {
        user_types_count: metaQuery.data?.user_types?.length ?? 0,
        meeting_platforms_count: metaQuery.data?.meeting_platforms?.length ?? 0,
        sample_user_types: metaQuery.data?.user_types?.slice(0, 5) ?? [],
      });
    }
  }, [DEBUG_PUBLIC_DEMOS, metaQuery.isSuccess, metaQuery.data]);

  useEffect(() => {
    if (isConfirmed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isConfirmed]);

  useEffect(() => {
    if (!isConfirmed) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleOk();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isConfirmed]);

  const timezones = useMemo(
    () => [
      { value: 'Europe/Paris', label: 'Europe/Paris' },
      { value: 'Europe/London', label: 'Europe/London' },
      { value: 'America/New_York', label: 'America/New_York' },
      { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
    ],
    []
  );

  const userTypes = useMemo(
    () => metaQuery.data?.user_types ?? [],
    [metaQuery.data]
  );
  const userTypesResolved = useMemo(() => {
    if (userTypes.length > 0) return userTypes;
    if (metaQuery.isLoading) return [];
    return DEFAULT_PUBLIC_DEMOS_USER_TYPES;
  }, [userTypes, metaQuery.isLoading]);
  const userTypeSelectOptions = useMemo(
    () => userTypesResolved.map((t) => ({ label: t.label, value: t.value })),
    [userTypesResolved]
  );

  const availabilityDays = useMemo(
    () => availabilityQuery.data?.days ?? [],
    [availabilityQuery.data]
  );

  const availableDateSet = useMemo(() => {
    const set = new Set<string>();
    for (const d of availabilityDays) set.add(d.date);
    return set;
  }, [availabilityDays]);

  const selectedDateIso = useMemo(() => {
    if (!formData.selectedDate) return null;
    const y = formData.selectedDate.getFullYear();
    const m = String(formData.selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(formData.selectedDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [formData.selectedDate]);

  const selectedDaySlots = useMemo(() => {
    if (!selectedDateIso) return [];
    const day = availabilityDays.find((d) => d.date === selectedDateIso);
    return day?.slots ?? [];
  }, [availabilityDays, selectedDateIso]);

  const selectedSlotLabel = useMemo(() => {
    if (!formData.selectedSlotId) return '';
    const slot = selectedDaySlots.find((s) => s.id === formData.selectedSlotId);
    if (!slot) return '';
    return `${slot.start_time} - ${slot.end_time}`;
  }, [formData.selectedSlotId, selectedDaySlots]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isTimezoneOpen) {
        if (
          timezoneRef.current &&
          !timezoneRef.current.contains(event.target as Node) &&
          timezoneButtonRef.current &&
          !timezoneButtonRef.current.contains(event.target as Node)
        ) {
          setIsTimezoneOpen(false);
        }
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsTimezoneOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isTimezoneOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserTypeSelect = (typeValue: string) => {
    setFormData((prev) => ({ ...prev, userType: typeValue }));
  };

  const handleCancel = () => {
    router.push(LOGIN_ROUTE);
  };

  const handleNext = async () => {
    setApiError(null);

    // Step 1 -> create session
    if (currentStep === 1) {
      try {
        const res = await createSessionMutation.mutateAsync({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          user_type: formData.userType,
          timezone: formData.timezone,
        });
        setSessionId(res.id);
        setCurrentStep(2);
      } catch (e) {
        setApiError(
          e instanceof Error
            ? e.message
            : 'Impossible de créer la session de démo.'
        );
      }
      return;
    }

    // Step 2 -> require slot selection
    if (currentStep === 2) {
      if (!formData.selectedDate || !formData.selectedSlotId) {
        setApiError('Veuillez sélectionner une date et un créneau.');
        return;
      }
      setCurrentStep(3);
      return;
    }

    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const handleConfirm = () => {
    void (async () => {
      setApiError(null);
      if (!sessionId) {
        setApiError('Session introuvable. Veuillez recommencer.');
        return;
      }
      if (!formData.selectedSlotId) {
        setApiError('Veuillez sélectionner un créneau.');
        return;
      }
      try {
        await patchSessionMutation.mutateAsync({
          sessionId,
          body: {
            slot_id: formData.selectedSlotId,
            meeting_platform: formData.platform,
            notes: formData.message,
            email_reminder_enabled: formData.notificationsEnabled,
            confirmed: true,
          },
        });
        setIsConfirmed(true);
      } catch (e) {
        setApiError(
          e instanceof Error ? e.message : 'Impossible de confirmer la démo.'
        );
      }
    })();
  };

  const handleOk = () => {
    router.push(LOGIN_ROUTE);
  };

  const handleMessageChange = (message: string) => {
    setFormData((prev) => ({ ...prev, message }));
  };

  const handleNotificationToggle = (enabled: boolean) => {
    setFormData((prev) => ({ ...prev, notificationsEnabled: enabled }));
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTimezoneSelect = (tz: string) => {
    setFormData((prev) => ({ ...prev, timezone: tz }));
    setIsTimezoneOpen(false);
  };

  const handleDateSelect = (date: Date) => {
    setFormData((prev) => ({
      ...prev,
      selectedDate: date,
      selectedSlotId: '',
    }));
  };

  const handleSlotSelect = (slotId: string) => {
    setFormData((prev) => ({ ...prev, selectedSlotId: slotId }));
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const calendarDays = getCalendarDays();
  const monthNames = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  return (
    <div className="planifier-demo-page">
      <header className="planifier-demo-header">
        <div className="planifier-demo-header-inner">
          <div className="planifier-demo-logo">
            <CarPulseLogo className="planifier-demo-logo-svg" />
          </div>
        </div>
      </header>

      <main className="planifier-demo-main">
        <div className="planifier-demo-container">
          <div className="planifier-demo-title-section">
            <h1 className="planifier-demo-title">Planifier une démo</h1>
          </div>

          <div className="planifier-demo-progress-container">
            <div className="planifier-demo-progress-bar">
              <div
                className="planifier-demo-progress-bar-filled"
                style={{ width: `${(currentStep / 3) * 100}%` }}
              ></div>
            </div>
            <span className="planifier-demo-progress-text">
              Etape {currentStep}/3
            </span>
          </div>

          {currentStep === 1 && (
            <>
              <div className="planifier-demo-section-title">
                <h2 className="planifier-demo-section-title-text">
                  Informations personnelles
                </h2>
              </div>

              <form
                className="planifier-demo-form"
                onSubmit={(e) => e.preventDefault()}
              >
                {apiError ? (
                  <div
                    className="planifier-demo-api-error"
                    role="alert"
                    style={{
                      marginBottom: 14,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid #FED7AA',
                      background: '#FFF7ED',
                      color: '#9A3412',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {apiError}
                  </div>
                ) : null}
                <div className="planifier-demo-form-grid planifier-demo-form-grid-desktop">
                  <div className="planifier-demo-form-column">
                    <div className="planifier-demo-form-field">
                      <Input
                        label="Nom"
                        placeholder="Votre nom"
                        type="text"
                        size="medium"
                        state="default"
                        showLeftIcon={false}
                        showRightIcon={false}
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange('lastName', e.target.value)
                        }
                      />
                    </div>

                    <div className="planifier-demo-form-field">
                      <Input
                        label="Email"
                        placeholder="ex: mailuser@domail"
                        type="email"
                        size="medium"
                        state="default"
                        showLeftIcon={false}
                        showRightIcon={false}
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange('email', e.target.value)
                        }
                      />
                    </div>

                    <div className="planifier-demo-form-field">
                      <label
                        id="user-type-label"
                        className="planifier-demo-form-label"
                        htmlFor="user-type-select"
                      >
                        Type d&apos;utilisateur
                      </label>
                      {metaQuery.isError ? (
                        <div
                          role="status"
                          style={{
                            marginBottom: 8,
                            padding: '8px 10px',
                            fontSize: 12,
                            color: '#9a3412',
                            background: '#fff7ed',
                            borderRadius: 8,
                            border: '1px solid #fed7aa',
                          }}
                        >
                          Impossible de charger la liste depuis le serveur —
                          types par défaut affichés.
                        </div>
                      ) : null}
                      <div
                        style={
                          metaQuery.isLoading
                            ? { opacity: 0.6, pointerEvents: 'none' as const }
                            : undefined
                        }
                      >
                        <MarkAsDropdown
                          className="planifier-demo-user-type-select"
                          value={formData.userType}
                          options={userTypeSelectOptions}
                          onChange={handleUserTypeSelect}
                          placeholder={
                            metaQuery.isLoading
                              ? 'Chargement…'
                              : 'Sélectionner utilisateur'
                          }
                          showChevron
                          useSelectedOptionIcon={false}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="planifier-demo-form-column">
                    <div className="planifier-demo-form-field">
                      <Input
                        label="Prénom"
                        placeholder="Votre prénom"
                        type="text"
                        size="medium"
                        state="default"
                        showLeftIcon={false}
                        showRightIcon={false}
                        value={formData.firstName}
                        onChange={(e) =>
                          handleInputChange('firstName', e.target.value)
                        }
                      />
                    </div>

                    <div className="planifier-demo-form-field">
                      <Input
                        label="Numéro de téléphone"
                        placeholder="Placeholder"
                        type="tel"
                        size="medium"
                        state="default"
                        showLeftIcon={false}
                        showRightIcon={false}
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange('phone', e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="planifier-demo-form-grid planifier-demo-form-grid-mobile">
                  <div className="planifier-demo-form-field">
                    <Input
                      label="Nom"
                      placeholder="Votre nom"
                      type="text"
                      size="medium"
                      state="default"
                      showLeftIcon={false}
                      showRightIcon={false}
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange('lastName', e.target.value)
                      }
                    />
                  </div>

                  <div className="planifier-demo-form-field">
                    <Input
                      label="Email"
                      placeholder="ex: mailuser@domail"
                      type="email"
                      size="medium"
                      state="default"
                      showLeftIcon={false}
                      showRightIcon={false}
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange('email', e.target.value)
                      }
                    />
                  </div>

                  <div className="planifier-demo-form-field">
                    <label
                      id="user-type-label-mobile"
                      className="planifier-demo-form-label"
                      htmlFor="user-type-select-mobile"
                    >
                      Type d&apos;utilisateur
                    </label>
                    {metaQuery.isError ? (
                      <div
                        role="status"
                        style={{
                          marginBottom: 8,
                          padding: '8px 10px',
                          fontSize: 12,
                          color: '#9a3412',
                          background: '#fff7ed',
                          borderRadius: 8,
                          border: '1px solid #fed7aa',
                        }}
                      >
                        Impossible de charger la liste depuis le serveur — types
                        par défaut affichés.
                      </div>
                    ) : null}
                    <div
                      style={
                        metaQuery.isLoading
                          ? { opacity: 0.6, pointerEvents: 'none' as const }
                          : undefined
                      }
                    >
                      <MarkAsDropdown
                        className="planifier-demo-user-type-select"
                        value={formData.userType}
                        options={userTypeSelectOptions}
                        onChange={handleUserTypeSelect}
                        placeholder={
                          metaQuery.isLoading
                            ? 'Chargement…'
                            : 'Sélectionner utilisateur'
                        }
                        showChevron
                        useSelectedOptionIcon={false}
                      />
                    </div>
                  </div>

                  <div className="planifier-demo-form-field">
                    <Input
                      label="Prénom"
                      placeholder="Votre prénom"
                      type="text"
                      size="medium"
                      state="default"
                      showLeftIcon={false}
                      showRightIcon={false}
                      value={formData.firstName}
                      onChange={(e) =>
                        handleInputChange('firstName', e.target.value)
                      }
                    />
                  </div>

                  <div className="planifier-demo-form-field">
                    <Input
                      label="Numéro de téléphone"
                      placeholder="Placeholder"
                      type="tel"
                      size="medium"
                      state="default"
                      showLeftIcon={false}
                      showRightIcon={false}
                      value={formData.phone}
                      onChange={(e) =>
                        handleInputChange('phone', e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="planifier-demo-actions">
                  <button
                    type="button"
                    className="planifier-demo-btn planifier-demo-btn-cancel"
                    onClick={handleCancel}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="planifier-demo-btn planifier-demo-btn-next"
                    onClick={() => void handleNext()}
                    disabled={createSessionMutation.isPending}
                  >
                    {createSessionMutation.isPending
                      ? 'Chargement…'
                      : 'Suivant'}
                  </button>
                </div>
              </form>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div className="planifier-demo-step2-container">
                <div className="planifier-demo-step2-left">
                  <div className="planifier-demo-section-title">
                    <h2 className="planifier-demo-section-title-text">
                      Sélectionner un créneau
                    </h2>
                  </div>

                  <div
                    className="planifier-demo-timezone-wrapper"
                    ref={timezoneRef}
                  >
                    <button
                      ref={timezoneButtonRef}
                      type="button"
                      className={`planifier-demo-timezone-btn ${isTimezoneOpen ? 'open' : ''}`}
                      onClick={() => setIsTimezoneOpen(!isTimezoneOpen)}
                    >
                      <Globe
                        size={16}
                        className="planifier-demo-timezone-icon"
                      />
                      <span>{formData.timezone}</span>
                      <ChevronDown
                        size={16}
                        className={`planifier-demo-timezone-chevron ${isTimezoneOpen ? 'open' : ''}`}
                      />
                    </button>
                    {isTimezoneOpen && (
                      <>
                        <div
                          className="planifier-demo-dropdown-overlay"
                          onClick={() => setIsTimezoneOpen(false)}
                        />
                        <div className="planifier-demo-timezone-menu">
                          {timezones.map((tz) => (
                            <button
                              key={tz.value}
                              type="button"
                              className={`planifier-demo-dropdown-option ${
                                formData.timezone === tz.value ? 'selected' : ''
                              }`}
                              onClick={() => handleTimezoneSelect(tz.value)}
                            >
                              {tz.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="planifier-demo-calendar">
                    <div className="planifier-demo-calendar-header">
                      <button
                        type="button"
                        className="planifier-demo-calendar-nav-btn"
                        onClick={() => handleMonthChange('prev')}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <h3 className="planifier-demo-calendar-month">
                        {monthNames[currentMonth.getMonth()]}{' '}
                        {currentMonth.getFullYear()}
                      </h3>
                      <button
                        type="button"
                        className="planifier-demo-calendar-nav-btn"
                        onClick={() => handleMonthChange('next')}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    <div className="planifier-demo-calendar-weekdays">
                      {dayNames.map((day) => (
                        <div
                          key={day}
                          className="planifier-demo-calendar-weekday"
                        >
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="planifier-demo-calendar-days">
                      {calendarDays.map((date, index) => {
                        if (!date) {
                          return (
                            <div
                              key={`empty-${index}`}
                              className="planifier-demo-calendar-day empty"
                            ></div>
                          );
                        }
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        const dayDate = new Date(date);
                        dayDate.setHours(0, 0, 0, 0);

                        const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const isSelected =
                          formData.selectedDate &&
                          date.getDate() === formData.selectedDate.getDate() &&
                          date.getMonth() ===
                            formData.selectedDate.getMonth() &&
                          date.getFullYear() ===
                            formData.selectedDate.getFullYear();
                        const isAvailable = availableDateSet.has(iso);
                        const isPast = dayDate.getTime() < today.getTime();
                        const isDisabled = isPast || !isAvailable;
                        return (
                          <button
                            key={date.toISOString()}
                            type="button"
                            className={`planifier-demo-calendar-day ${isSelected ? 'selected' : ''} ${
                              isPast ? 'past' : !isAvailable ? 'disabled' : ''
                            }`}
                            onClick={() => handleDateSelect(date)}
                            disabled={isDisabled || availabilityQuery.isLoading}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="planifier-demo-step2-right">
                  <h2 className="planifier-demo-time-title">
                    Sélectionner l'heure
                  </h2>
                  <div className="planifier-demo-time-slots">
                    {availabilityQuery.isLoading ? (
                      <>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div
                            key={`slot-skel-${i}`}
                            style={{ marginBottom: 10 }}
                          >
                            <Skeleton className="h-10 w-full rounded-xl" />
                          </div>
                        ))}
                      </>
                    ) : selectedDateIso && selectedDaySlots.length > 0 ? (
                      selectedDaySlots.map((slot) => {
                        const label = `${slot.start_time} - ${slot.end_time}`;
                        const timeId = `time-slot-${slot.id}`;
                        return (
                          <label
                            key={slot.id}
                            htmlFor={timeId}
                            className={`planifier-demo-time-slot ${formData.selectedSlotId === slot.id ? 'selected' : ''}`}
                          >
                            <input
                              id={timeId}
                              type="radio"
                              name="timeSlot"
                              value={slot.id}
                              checked={formData.selectedSlotId === slot.id}
                              onChange={() => handleSlotSelect(slot.id)}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })
                    ) : (
                      <div
                        style={{
                          color: '#7A7A7A',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {formData.selectedDate
                          ? 'Aucun créneau disponible ce jour-là.'
                          : 'Sélectionnez une date.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="planifier-demo-actions">
                <button
                  type="button"
                  className="planifier-demo-btn planifier-demo-btn-back"
                  onClick={handleBack}
                >
                  Retour
                </button>
                <button
                  type="button"
                  className="planifier-demo-btn planifier-demo-btn-next"
                  onClick={() => void handleNext()}
                >
                  Suivant
                </button>
              </div>
            </>
          )}

          {currentStep === 3 && (
            <>
              <div className="planifier-demo-step3-container">
                <div className="planifier-demo-step3-left">
                  <div className="planifier-demo-section-title">
                    <h2 className="planifier-demo-section-title-text">
                      Confirmation du rendez-vous
                    </h2>
                  </div>

                  <div className="planifier-demo-message-section">
                    <label
                      htmlFor="demo-message"
                      className="planifier-demo-message-title"
                    >
                      Envoyer un message ou points particuliers{' '}
                      <span className="planifier-demo-message-optional">
                        (facultatif)
                      </span>
                    </label>
                    <textarea
                      id="demo-message"
                      name="message"
                      className="planifier-demo-message-textarea"
                      placeholder="Placeholder"
                      value={formData.message}
                      onChange={(e) => handleMessageChange(e.target.value)}
                      rows={6}
                    />
                  </div>
                </div>

                <div className="planifier-demo-step3-right">
                  <div className="planifier-demo-meeting-details">
                    <h3 className="planifier-demo-meeting-details-title">
                      Détails de la réunion
                    </h3>
                    <div className="planifier-demo-meeting-detail-item">
                      <Calendar
                        size={20}
                        className="planifier-demo-meeting-icon"
                      />
                      <div className="planifier-demo-meeting-detail-content">
                        <span className="planifier-demo-meeting-detail-label">
                          Date
                        </span>
                        <span className="planifier-demo-meeting-detail-value">
                          {formData.selectedDate
                            ? formatDate(formData.selectedDate)
                            : 'Non sélectionnée'}
                        </span>
                      </div>
                    </div>
                    <div className="planifier-demo-meeting-detail-item">
                      <Clock
                        size={20}
                        className="planifier-demo-meeting-icon"
                      />
                      <div className="planifier-demo-meeting-detail-content">
                        <span className="planifier-demo-meeting-detail-label">
                          Heure
                        </span>
                        <span className="planifier-demo-meeting-detail-value">
                          {selectedSlotLabel
                            ? `${selectedSlotLabel} (${formData.timezone})`
                            : 'Non sélectionnée'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="planifier-demo-notification-box">
                    <Bell
                      size={20}
                      className="planifier-demo-notification-icon"
                    />
                    <div className="planifier-demo-notification-content">
                      <span className="planifier-demo-notification-title">
                        Notification
                      </span>
                      <span className="planifier-demo-notification-text">
                        Être notifié par e-mail avant la session
                      </span>
                    </div>
                    <Toggle
                      checked={formData.notificationsEnabled}
                      onChange={handleNotificationToggle}
                      className="planifier-demo-notification-toggle"
                    />
                  </div>
                </div>
              </div>

              <div className="planifier-demo-actions">
                <button
                  type="button"
                  className="planifier-demo-btn planifier-demo-btn-back"
                  onClick={handleBack}
                >
                  Retour
                </button>
                <button
                  type="button"
                  className="planifier-demo-btn planifier-demo-btn-confirm"
                  onClick={handleConfirm}
                  disabled={patchSessionMutation.isPending}
                >
                  {patchSessionMutation.isPending ? 'Chargement…' : 'Confirmer'}
                </button>
              </div>
            </>
          )}

          {isConfirmed &&
            mounted &&
            typeof window !== 'undefined' &&
            createPortal(
              <div
                className="planifier-demo-confirmation-overlay"
                onClick={handleOk}
              >
                <div
                  className="planifier-demo-confirmation-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="planifier-demo-confirmation-icon">
                    <svg
                      width="128"
                      height="128"
                      viewBox="0 0 128 128"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20.7998 102.933H98.7732C103.147 102.933 106.56 99.4134 106.56 95.1467V28.6934C106.56 24.3201 103.04 20.9067 98.7732 20.9067H20.7998C16.4265 20.9067 13.0132 24.4267 13.0132 28.6934V95.0401C13.0132 99.4134 16.5332 102.933 20.7998 102.933Z"
                        fill="#FFEED4"
                      />
                      <g filter="url(#filter0_i_769_4264)">
                        <path
                          d="M106.667 28.3734V44.4801H13.0132V28.3734C13.0132 24.2134 16.4265 20.9067 20.4799 20.9067H99.0932C103.253 20.8001 106.667 24.2134 106.667 28.3734Z"
                          fill="url(#paint0_linear_769_4264)"
                        />
                      </g>
                      <path
                        d="M20.48 21.1562H99.0933V21.1572L99.0991 21.1562C103.115 21.0533 106.416 24.35 106.417 28.373V44.2305H13.2632V28.373C13.2634 24.3536 16.5625 21.1562 20.48 21.1562Z"
                        stroke="#FF8E2B"
                        strokeWidth="0.5"
                      />
                      <path
                        d="M37.3332 36.4801C39.4539 36.4801 41.1732 34.7608 41.1732 32.64C41.1732 30.5193 39.4539 28.8 37.3332 28.8C35.2124 28.8 33.4932 30.5193 33.4932 32.64C33.4932 34.7608 35.2124 36.4801 37.3332 36.4801Z"
                        fill="#FE5E00"
                      />
                      <path
                        d="M59.84 36.4801C61.9608 36.4801 63.68 34.7608 63.68 32.64C63.68 30.5193 61.9608 28.8 59.84 28.8C57.7192 28.8 56 30.5193 56 32.64C56 34.7608 57.7192 36.4801 59.84 36.4801Z"
                        fill="#FE5E00"
                      />
                      <path
                        d="M82.3463 36.4801C84.4671 36.4801 86.1864 34.7608 86.1864 32.64C86.1864 30.5193 84.4671 28.8 82.3463 28.8C80.2256 28.8 78.5063 30.5193 78.5063 32.64C78.5063 34.7608 80.2256 36.4801 82.3463 36.4801Z"
                        fill="#FE5E00"
                      />
                      <path
                        d="M38.3999 16.1067H36.2666C35.6266 16.1067 35.0933 16.64 35.0933 17.28V32.1067C35.0933 32.7467 35.6266 33.28 36.2666 33.28H38.3999C39.0399 33.28 39.5733 32.7467 39.5733 32.1067V17.3867C39.6799 16.64 39.0399 16.1067 38.3999 16.1067Z"
                        fill="#FFEED4"
                      />
                      <path
                        d="M60.9068 16.1067H58.7734C58.1334 16.1067 57.6001 16.64 57.6001 17.28V32.1067C57.6001 32.7467 58.1334 33.28 58.7734 33.28H60.9068C61.5468 33.28 62.0801 32.7467 62.0801 32.1067V17.3867C62.0801 16.64 61.5468 16.1067 60.9068 16.1067Z"
                        fill="#FFEED4"
                      />
                      <path
                        d="M83.4131 16.1067H81.2798C80.6398 16.1067 80.1064 16.64 80.1064 17.28V32.1067C80.1064 32.7467 80.6398 33.28 81.2798 33.28H83.4131C84.0531 33.28 84.5865 32.7467 84.5865 32.1067V17.3867C84.5865 16.64 84.0531 16.1067 83.4131 16.1067Z"
                        fill="#FFEED4"
                      />
                      <g filter="url(#filter1_i_769_4264)">
                        <path
                          d="M39.4664 55.2534H30.293C29.2264 55.2534 28.373 56.1068 28.373 57.1734V66.3468C28.373 67.4134 29.2264 68.2668 30.293 68.2668H39.4664C40.533 68.2668 41.3864 67.4134 41.3864 66.3468V57.1734C41.493 56.2134 40.6397 55.2534 39.4664 55.2534Z"
                          fill="url(#paint1_linear_769_4264)"
                        />
                      </g>
                      <path
                        d="M30.293 55.5034H39.4668C40.5094 55.5036 41.2259 56.352 41.1377 57.146L41.1367 57.1597V66.3472C41.1365 67.2754 40.3951 68.0169 39.4668 68.0171H30.293C29.3645 68.017 28.6233 67.2755 28.623 66.3472V57.1733C28.6231 56.2448 29.3644 55.5035 30.293 55.5034Z"
                        stroke="#FF8E2B"
                        strokeWidth="0.5"
                      />
                      <g filter="url(#filter2_i_769_4264)">
                        <path
                          d="M64.4263 55.2534H55.253C54.1863 55.2534 53.333 56.1068 53.333 57.1734V66.3468C53.333 67.4134 54.1863 68.2668 55.253 68.2668H64.4263C65.493 68.2668 66.3463 67.4134 66.3463 66.3468V57.1734C66.453 56.2134 65.493 55.2534 64.4263 55.2534Z"
                          fill="url(#paint2_linear_769_4264)"
                        />
                      </g>
                      <path
                        d="M55.2529 55.5034H64.4268C64.8857 55.5035 65.3306 55.7111 65.6494 56.0298C65.9693 56.3497 66.1407 56.7587 66.0977 57.146L66.0967 57.1597V66.3472C66.0965 67.2754 65.355 68.0169 64.4268 68.0171H55.2529C54.3245 68.017 53.5832 67.2755 53.583 66.3472V57.1733C53.583 56.2448 54.3244 55.5035 55.2529 55.5034Z"
                        stroke="#FF8E2B"
                        strokeWidth="0.5"
                      />
                      <g filter="url(#filter3_i_769_4264)">
                        <path
                          d="M89.3863 55.2534H80.213C79.1463 55.2534 78.293 56.1068 78.293 57.1734V66.3468C78.293 67.4134 79.1463 68.2668 80.213 68.2668H89.3863C90.453 68.2668 91.3063 67.4134 91.3063 66.3468V57.1734C91.3063 56.2134 90.453 55.2534 89.3863 55.2534Z"
                          fill="url(#paint3_linear_769_4264)"
                        />
                      </g>
                      <path
                        d="M80.2129 55.5034H89.3867C90.3063 55.5037 91.0566 56.3426 91.0566 57.1733V66.3472C91.0564 67.2754 90.315 68.0169 89.3867 68.0171H80.2129C79.2845 68.0171 78.5432 67.2755 78.543 66.3472V57.1733C78.543 56.2448 79.2844 55.5035 80.2129 55.5034Z"
                        stroke="#FF8E2B"
                        strokeWidth="0.5"
                      />
                      <g filter="url(#filter4_i_769_4264)">
                        <path
                          d="M39.4664 79.4668H30.293C29.2264 79.4668 28.373 80.3201 28.373 81.3868V90.5601C28.373 91.6268 29.2264 92.4801 30.293 92.4801H39.4664C40.533 92.4801 41.3864 91.6268 41.3864 90.5601V81.3868C41.493 80.3201 40.6397 79.4668 39.4664 79.4668Z"
                          fill="url(#paint4_linear_769_4264)"
                        />
                      </g>
                      <path
                        d="M30.293 79.7168H39.4668C40.5173 79.717 41.2272 80.467 41.1377 81.3623L41.1367 81.374V90.5605C41.1365 91.4888 40.3951 92.2302 39.4668 92.2305H30.293C29.3645 92.2304 28.6233 91.4889 28.623 90.5605V81.3867C28.6231 80.4582 29.3644 79.7168 30.293 79.7168Z"
                        stroke="#FF8E2B"
                        strokeWidth="0.5"
                      />
                      <g filter="url(#filter5_i_769_4264)">
                        <path
                          d="M64.4263 79.4668H55.253C54.1863 79.4668 53.333 80.3201 53.333 81.3868V90.5601C53.333 91.6268 54.1863 92.4801 55.253 92.4801H64.4263C65.493 92.4801 66.3463 91.6268 66.3463 90.5601V81.3868C66.453 80.3201 65.493 79.4668 64.4263 79.4668Z"
                          fill="url(#paint5_linear_769_4264)"
                        />
                      </g>
                      <path
                        d="M55.2529 79.7168H64.4268C65.3805 79.717 66.1863 80.4758 66.0977 81.3623L66.0967 81.374V90.5605C66.0965 91.4888 65.355 92.2302 64.4268 92.2305H55.2529C54.3245 92.2304 53.5832 91.4889 53.583 90.5605V81.3867C53.5831 80.4582 54.3244 79.7168 55.2529 79.7168Z"
                        stroke="#FF8E2B"
                        strokeWidth="0.5"
                      />
                      <path
                        d="M89.3863 79.4668H80.213C79.1463 79.4668 78.293 80.3201 78.293 81.3868V90.5601C78.293 91.6268 79.1463 92.4801 80.213 92.4801H89.3863C90.453 92.4801 91.3063 91.6268 91.3063 90.5601V81.3868C91.3063 80.3201 90.453 79.4668 89.3863 79.4668Z"
                        fill="#FF8E2B"
                      />
                      <g filter="url(#filter6_i_769_4264)">
                        <path
                          d="M94.08 111.893C105.626 111.893 114.987 102.533 114.987 90.9868C114.987 79.4403 105.626 70.0801 94.08 70.0801C82.5336 70.0801 73.1733 79.4403 73.1733 90.9868C73.1733 102.533 82.5336 111.893 94.08 111.893Z"
                          fill="#22C55E"
                        />
                      </g>
                      <path
                        d="M94.0796 70.3301C105.488 70.3301 114.737 79.5782 114.737 90.9863C114.737 102.395 105.488 111.644 94.0796 111.644C82.6714 111.643 73.4233 102.395 73.4233 90.9863C73.4236 79.5783 82.6716 70.3303 94.0796 70.3301Z"
                        stroke="#86EFAC"
                        strokeWidth="0.5"
                      />
                      <path
                        d="M91.6265 100.373H91.5198C90.6665 100.373 89.9198 99.9468 89.3865 99.3068L80.9598 89.8135C79.8931 88.5335 79.9998 86.6135 81.1731 85.5468C82.4531 84.4801 84.3731 84.5868 85.4398 85.7601L91.8398 92.9068L102.826 82.3468C104 81.1735 105.92 81.2801 107.093 82.4535C108.266 83.6268 108.16 85.5468 106.986 86.7201L93.7598 99.6268C93.2265 100.16 92.4798 100.373 91.6265 100.373Z"
                        fill="white"
                      />
                      <defs>
                        <filter
                          id="filter0_i_769_4264"
                          x="13.0132"
                          y="20.9043"
                          width="93.6533"
                          height="24.5757"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <filter
                          id="filter1_i_769_4264"
                          x="28.373"
                          y="55.2534"
                          width="13.0225"
                          height="14.0134"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <filter
                          id="filter2_i_769_4264"
                          x="53.333"
                          y="55.2534"
                          width="13.0215"
                          height="14.0134"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <filter
                          id="filter3_i_769_4264"
                          x="78.293"
                          y="55.2534"
                          width="13.0132"
                          height="14.0134"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <filter
                          id="filter4_i_769_4264"
                          x="28.373"
                          y="79.4668"
                          width="13.0225"
                          height="14.0134"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <filter
                          id="filter5_i_769_4264"
                          x="53.333"
                          y="79.4668"
                          width="13.0215"
                          height="14.0134"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <filter
                          id="filter6_i_769_4264"
                          x="73.1733"
                          y="70.0801"
                          width="41.8135"
                          height="42.8132"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1" />
                          <feGaussianBlur stdDeviation="1.25" />
                          <feComposite
                            in2="hardAlpha"
                            operator="arithmetic"
                            k2="-1"
                            k3="1"
                          />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="shape"
                            result="effect1_innerShadow_769_4264"
                          />
                        </filter>
                        <linearGradient
                          id="paint0_linear_769_4264"
                          x1="13.0132"
                          y1="44.4801"
                          x2="23.5929"
                          y2="0.788409"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FE5E00" />
                          <stop offset="1" stopColor="#FF8E2B" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_769_4264"
                          x1="28.373"
                          y1="68.2668"
                          x2="41.1286"
                          y2="54.9969"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FE5E00" />
                          <stop offset="1" stopColor="#FF8E2B" />
                        </linearGradient>
                        <linearGradient
                          id="paint2_linear_769_4264"
                          x1="53.333"
                          y1="68.2668"
                          x2="66.0886"
                          y2="54.9978"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FE5E00" />
                          <stop offset="1" stopColor="#FF8E2B" />
                        </linearGradient>
                        <linearGradient
                          id="paint3_linear_769_4264"
                          x1="78.293"
                          y1="68.2668"
                          x2="91.0488"
                          y2="55.0058"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FE5E00" />
                          <stop offset="1" stopColor="#FF8E2B" />
                        </linearGradient>
                        <linearGradient
                          id="paint4_linear_769_4264"
                          x1="28.373"
                          y1="92.4801"
                          x2="41.1286"
                          y2="79.2103"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FE5E00" />
                          <stop offset="1" stopColor="#FF8E2B" />
                        </linearGradient>
                        <linearGradient
                          id="paint5_linear_769_4264"
                          x1="53.333"
                          y1="92.4801"
                          x2="66.0886"
                          y2="79.2112"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FE5E00" />
                          <stop offset="1" stopColor="#FF8E2B" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <h1 className="planifier-demo-confirmation-title">
                    Votre démo a bien été programmée !
                  </h1>
                  <p className="planifier-demo-confirmation-text">
                    Nous vous avons envoyé un email de confirmation contenant
                    les détails du rendez-vous. Un membre de l'équipe CarPulse
                    vous contactera le jour J.
                  </p>
                  <button
                    type="button"
                    className="planifier-demo-confirmation-btn"
                    onClick={handleOk}
                  >
                    Ok
                  </button>
                </div>
              </div>,
              document.body
            )}
        </div>
      </main>
    </div>
  );
}
